/**
 * CompanyDashboardAllObjectiveDrawer
 *
 * Bottom-sheet drawer showing a selected objective: metadata, key results list,
 * and related initiatives. Supports creating/editing child items and keeps local
 * state in sync by re-fetching the objective after mutations.
 */
import React, { useState } from "react";
import {
  addInitiative,
  addKeyresult,
  getObjectivesById,
} from "../../../../api/objectives";
import CompanyDashboardAllObjectiveDrawerHeader from "./company-dashboard-all-objective-drawer-header/company-dashboard-all-objective-drawer-header";
import CompanyDashboardAllObjectiveDrawerSideBar from "./company-dashboard-all-objective-drawer-side-bar/company-dashboard-all-objective-drawer-side-bar";
import CompanyDashboardAllObjectiveDrawerMainContent from "./company-dashboard-all-objective-drawer-main-content/company-dashboard-all-objective-drawer-main-content";
import "./company-dashboard-all-objective-drawer.scss";
import {
  formatInitiativePayload,
  formatKeyResultPayloadData,
} from "../../company-dashboard-utils";
import { updateInitiative } from "../../../../api/initiative";
import dayjs from "dayjs";

const CompanyDashboardAllObjectiveDrawer: any = (props: any) => {
  const {
    setIsShowAllObjectiveDrawer,
    objectId,
    setAlertMessage,
    setAlertType,
    setAlertOpen,
    setLoading,
    employeesList,
    statusListObjectives,
    statusListKeyResults,
    statusListInitiatives,
    fetchObjectives,
    departmentsList,
    fetchUpcomingMilestones,
    fetchAllYearsForObjectives,
    fetchAllYearsForInitiatives,
  } = props;
  const [objectivesData, setObjectivesData] = useState<any>(null);
  const [listOfKeyResultsData, setListOfKeyResultsData] = useState<any[]>([]);
  const [descriptionData, setDescriptionData] = useState<any>("");
  const [selectedKeyResult, setSelectedKeyResult] = useState<any>(null);

  const fetchObjectivesById = async (lastKeyResult?: any) => {
    setLoading(true);
    try {
      const response = await getObjectivesById({ objectId });
      const keyResults = response?.data?.data?.keyResults || [];

      // ✅ apply selection if we already have one
      const normalizedKeyResults = keyResults.map((kr: any) => ({
        ...kr,
        id: Number(kr.pk), // or Number(kr.id) depending on your format
        isSelected: lastKeyResult
          ? Number(kr.pk) === Number(lastKeyResult.id)
          : false,
      }));

      setObjectivesData(response?.data?.data);
      setDescriptionData(response?.data?.data?.objectiveDescription || "");
      setListOfKeyResultsData(normalizedKeyResults);
      setAlertMessage("Fetched objective successfully");
      setAlertType("success");
      setAlertOpen(true);
      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      setObjectivesData(null);
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch objective",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };
  const handleCreateObjectiveKeyresult = async (formValues: any) => {
    setLoading(true);
    try {
      const payload = formatKeyResultPayloadData(formValues);
      await addKeyresult(payload);
      setAlertMessage("Key Result created successfully");
      setAlertType("success");
      setAlertOpen(true);
      fetchObjectivesById();
      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to create Key Result",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };
  const handleCreateInitiative = async (
    formValues: any,
    keyResultId: number,
  ) => {
    setLoading(true);
    try {
      const payload = await formatInitiativePayload(
        formValues,
        objectivesData?.pk, // or wherever your current objectiveId comes from
        selectedKeyResult?.pk, // or wherever your current keyResultId comes from// whoever is creating
      );
      await addInitiative(payload); // <-- API call
      setAlertMessage("Initiative created successfully");
      setAlertType("success");
      setAlertOpen(true);
      fetchObjectivesById(selectedKeyResult);
      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to create Initiative",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };
  const handleEditInitiative = async (formValues: any) => {
    setLoading(true);
    try {
      const payload = {
        description: formValues.description,
        owners: formValues.owners || [],
        startDate: dayjs(formValues.startDate || dayjs()).format("YYYY-MM-DD"),
        endDate: dayjs(formValues.endDate || dayjs()).format("YYYY-MM-DD"),
        departmentIds: formValues.departmentIds,
        title: formValues.title,
        status: formValues?.status?.statusName || "",
        weightage: formValues.weightage,
        threshold: formValues.threshold,
        participatingDepartments: formValues.participatingDepartments,
      };

      await updateInitiative(formValues.id, payload);
      setAlertMessage("Initiative updated successfully");
      setAlertType("success");
      setAlertOpen(true);

      fetchObjectivesById();
      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to update Initiative",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };
  // }, [objectId]);

  return (
    <div className="company-dashboard-all-objective-drawer">
      <div className="company-dashboard-all-objective-drawer-content">
        <CompanyDashboardAllObjectiveDrawerHeader
          setIsShowAllObjectiveDrawer={setIsShowAllObjectiveDrawer}
          handleOpenAddPopup={() => {}}
          fetchObjectives={fetchObjectives}
          fetchUpcomingMilestones={fetchUpcomingMilestones}
          fetchAllYearsForObjectives={fetchAllYearsForObjectives}
          fetchAllYearsForInitiatives={fetchAllYearsForInitiatives}
        />
        <div className="company-dashboard-all-objective-drawer-content-body-section">
          <CompanyDashboardAllObjectiveDrawerSideBar
            setIsShowAllObjectiveDrawer={setIsShowAllObjectiveDrawer}
            objectivesData={objectivesData}
            setObjectivesData={setObjectivesData}
            descriptionData={descriptionData}
            setDescriptionData={setDescriptionData}
            setInitiativeData={() => {}}
            listOfKeyResultsData={listOfKeyResultsData}
            setListOfKeyResultsData={setListOfKeyResultsData}
            employeesList={employeesList}
            statusListObjectives={statusListObjectives}
            statusListKeyResults={statusListKeyResults}
            statusListInitiatives={statusListInitiatives}
            saveKeyresult={handleCreateObjectiveKeyresult}
            selectedKeyResult={selectedKeyResult}
            setSelectedKeyResult={setSelectedKeyResult}
            fetchObjectivesById={fetchObjectivesById}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertType}
            setAlertOpen={setAlertOpen}
            setLoading={setLoading}
            departmentsList={departmentsList}
          />
          <CompanyDashboardAllObjectiveDrawerMainContent
            listOfKeyResultsData={listOfKeyResultsData}
            setListOfKeyResultsData={setListOfKeyResultsData}
            statusListInitiatives={statusListInitiatives}
            employeesList={employeesList}
            handleCreateInitiative={handleCreateInitiative}
            handleEditInitiative={handleEditInitiative}
            objectId={objectId}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertType}
            setAlertOpen={setAlertOpen}
            setLoading={setLoading}
            statusListObjectives={statusListObjectives}
            statusListKeyResults={statusListKeyResults}
            departmentsList={departmentsList}
            fetchObjectivesById={fetchObjectivesById}
          />
        </div>
      </div>
    </div>
  );
};

export default CompanyDashboardAllObjectiveDrawer;
